import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { h4, h5, paragraphSmall } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import CardGrid from '@stories/Components/CardGrid/CardGrid';
import { cardWidth } from '@stories/Components/CardGrid/CardGrid.styles';
import FormStyles from '@stories/Components/Misc/Forms/Form.styles';
import { ContentType } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import styled from 'styled-components';
// Link must overlay the entire card, to ensure that the buttons for the tooltips are accessible
const Link = styled.a `
  grid-column: 1;
  grid-row: 1 / span 4;
  margin: calc(var(--paddingY) * -1) calc(var(--paddingX) * -1);
  padding: var(--paddingY) var(--paddingX);
  text-decoration: none;

  // uses subgrid to ensure the fake button aligns correctly with other elements
  display: grid;
  grid-template-rows: subgrid;

  // fake button must sit within the link for hover styles to work as expected
  ${ButtonStyles.Container} {
    grid-row: 4;
    margin: ${fluid(16, 24)} auto 0 0;

    @media ${from(Device.TabletLarge)} {
      margin-left: auto;
      margin-right: 0;
    }
  }
`;
const Container = styled.div `
  --borderRadius: ${fluid(12, 20)};
  --paddingX: ${fluid(18, 26)};
  --paddingY: 26px;

  display: grid;
  grid-template-rows: repeat(4, min-content);
  background-color: var(--backgroundColour);
  color: ${brand.blue.navy};
  border-radius: var(--borderRadius) var(--borderRadius) var(--borderRadius) 0;
  padding: var(--paddingY) var(--paddingX);

  &[data-type=${ContentType.Default}] {
    --backgroundColour: ${brand.green.light};
    --featureColour: ${brand.green.dark};
  }

  &[data-type=${ContentType.Savings}] {
    --backgroundColour: ${brand.blue.light};
    --featureColour: ${brand.blue.dark};
  }

  /* Mobile click styles - whole card should mimic button press when link has active state */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    &:has(${Link}:active) {
      transform: translateY(2px);
    }
  }
`;
const Title = styled.h2 `
  ${h4};

  grid-row: 1;
  grid-column: 1;
  margin: 0;
`;
const SummaryBullets = styled.ul `
  ${paragraphSmall};
  ${fonts.proximaNova.semiBold};

  --gap: ${fluid(4, 8)} ${fluid(8, 18)};

  grid-row: 2;
  grid-column: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--gap);
  margin: 16px 0 0;

  li {
    display: inline-flex;
    align-items: center;
    gap: var(--gap);

    &:not(:last-of-type)::after {
      content: '\\2022';
      color: ${brand.green.dark};
      font-size: 115%;
      margin-top: -2px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    margin-top: 12px;
  }
`;
const Features = styled(CardGrid) `
  --maxWidth: 33%;
  --rowItems: 2;
  --alignment: flex-start;

  grid-row: 3;
  grid-column: 1;
  width: 100%;
  margin-top: ${fluid(16, 28)};

  @media ${from(Device.TabletSmall)} {
    --rowItems: 3 !important; // Important required to prevent CLS specificity issue
  }

  @media ${from(Device.Tablet)} {
    --rowItems: 4 !important;
  }

  @media ${from(Device.TabletLarge)} {
    --rowItems: 5 !important;
  }

  @media ${from(Device.Desktop)} {
    --rowItems: 6 !important;
  }
`;
const Info = styled(FormStyles.Info) `
  grid-column: 2;
  margin: 2px 0 0;
`;
const InfoButton = styled(FormStyles.InfoButton) ``;
const Feature = styled.div `
  ${cardWidth};

  display: grid;
  grid-template-columns: 1fr min-content;
  grid-template-rows: min-content 1fr;
  column-gap: ${fluid(8, 16)};
  background-color: ${brand.global.white};
  padding: 4px 12px;
  border-radius: 10px 10px 10px 0;
  z-index: 1;

  /* Larger z-index for feature displaying a tooltip to ensure info buttons don't overlap the tooltip when features stack */
  &:has(${Info}:hover, ${Info}:focus-visible, ${Info}:active) {
    z-index: 2;
  }
`;
const FeatureTitle = styled.h3 `
  ${paragraphSmall};
  ${fonts.proximaNova.bold};

  grid-column: 1;
  margin: 0;
`;
const FeatureContent = styled.p `
  ${h5};

  grid-column: 1 / span 2;
  margin: 0;
  color: var(--featureColour);
`;
const Tooltip = styled(FormStyles.Tooltip) `
  --width: min(50vw, calc(var(--siteWidth) * 0.5));

  width: calc(var(--width) - (var(--paddingX) * 2) - var(--sitePadding));
  z-index: 1;

  @media ${from(Device.TabletSmall)} {
    --width: min(33vw, calc(var(--siteWidth) * 0.33));
  }

  @media ${from(Device.Tablet)} {
    --width: min(27.5vw, calc(var(--siteWidth) * 0.275));
  }

  @media ${from(Device.TabletLarge)} {
    --width: min(25vw, calc(var(--siteWidth) * 0.25));
  }
`;
const ProductCardStyles = {
    Container,
    Features,
    Feature,
    FeatureTitle,
    FeatureContent,
    Info,
    Tooltip,
    Link,
    Title,
    SummaryBullets,
    InfoButton,
};
export default ProductCardStyles;
